import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, PropsWithChildren,
} from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { toStrategyKey } from '@/lib/strategyKeys';
import { generateIndicatorsForStrategy } from '@/lib/indicators';
import { normalizeIndicators } from '@/utils/normalizeIndicators';
import { stripIdsForDB } from '@/lib/pti';

const ensureArray = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
};

const len = (v: any): number => (Array.isArray(v) ? v.length : 0);

type RunMode = 'live' | 'paper';
type DeployTarget = 'live' | 'paper';

export type Bot = {
  id: string;
  title: string | null;
  name?: string | null;
  status?: string | null;
  exchange?: string | null;
  strategy?: string | null;
  runMode?: RunMode | null;
  deploymentTarget?: DeployTarget | null;
  createdAt?: string | null;
  is_active?: boolean;
  isActive?: boolean;
  is_running?: boolean;
  isRunning?: boolean;
  is_practice?: boolean;
  isPractice?: boolean;
  profit?: number;
  total_trades?: number;
  win_rate?: number;
  isConnected?: boolean;
  selected_coins?: string[];
  selectedCoins?: string[];
  quote_currency?: string;
  quoteCurrency?: string;
  primary_time_indicators?: any[];
  primaryTimeIndicators?: any[];
  secondary_time_enabled?: boolean;
  secondaryTimeEnabled?: boolean;
  signal_only_mode?: boolean;
  signalOnlyMode?: boolean;
  is_from_marketplace?: boolean;
  is_premium?: boolean;
};

type RawRow = {
  id?: string | null;
  user_id?: string | null;
  name?: string | null;
  exchange?: string | null;
  strategy?: string | null;
  deployment_target?: DeployTarget | null;
  quote_currency?: string | null;
  primary_time_interval?: string | null;
  selected_coins?: any;
  created_at?: string | null;
  updated_at?: string | null;
  is_practice?: boolean | null;
  is_active?: boolean | null;
  is_running?: boolean | null;
};

type SaveBotPayload = {
  id?: string;
  name?: string;
  title?: string;
  exchange?: string | null;
  strategy?: string | null;
  runMode?: RunMode | null;
  deploymentTarget?: DeployTarget | null;
  selectedCoins?: string[];
  primaryTimeIndicators?: any[];
  settings?: Record<string, unknown>;
  [key: string]: any;
};

export type BotsContextType = {
  bots: Bot[];
  practiceBots: Bot[];
  activeBots: Bot[];
  loading: boolean;
  isLoading: boolean;
  error: string | null;
  currentBot: Bot | null;
  backtestResults: any[];

  fetchBots: () => Promise<void>;
  refreshBots: () => Promise<void>;
  getBotById: (id: string) => Bot | undefined;

  saveBot: (payload: SaveBotPayload) => Promise<string>;
  createBot: (payload: SaveBotPayload) => Promise<{ id: string }>;
  updateBot: (botId: string, payload: SaveBotPayload) => Promise<{ id: string }>;

  activateFromBacktest: (args: {
    backtestId: string;
    backtestTitle?: string;
    desiredTarget?: DeployTarget;
    runMode?: RunMode;
    userId?: string;
  }) => Promise<{ id: string }>;

  openInMyBots: (args: {
    backtestId: string;
    backtestTitle?: string;
    desiredTarget?: DeployTarget;
    runMode?: RunMode;
    userId?: string;
  }) => Promise<{ id: string }>;

  saveThenRunBacktest: (params: {
    currentBotId?: string | null;
    name: string;
    exchange?: string | null;
    strategy?: string | null;
    runMode: RunMode;
    deploymentTarget?: DeployTarget;
    settings: any;
  }) => Promise<{ botId: string; backtestId: string }>;

  createOrUpdateBot: (params: {
    botId?: string | null;
    name: string;
    exchange?: string | null;
    strategy?: string | null;
    runMode: RunMode;
    deploymentTarget?: DeployTarget;
    settings: any;
  }) => Promise<string>;

  editBot: (botId: string) => Promise<void>;
  clearCurrentBot: () => void;
  deleteBot: (botId: string, opts?: { hard?: boolean; forceClose?: boolean }) => Promise<any>;
  toggleBotStatus: (botId: string) => Promise<void>;
  toggleBotRunning: (botId: string, running?: boolean) => Promise<{ success: boolean; is_running: boolean }>;
  setPracticeMode: (isPractice: boolean) => void;
};

const noopAsync = async () => ({ id: '' });
const noopAsyncString = async () => '';
const noopAsyncVoid = async () => {};
const noopVoid = () => {};

const defaultCtx: BotsContextType = {
  bots: [],
  practiceBots: [],
  activeBots: [],
  loading: false,
  isLoading: false,
  error: null,
  currentBot: null,
  backtestResults: [],
  fetchBots: noopAsyncVoid,
  refreshBots: noopAsyncVoid,
  getBotById: () => undefined,
  saveBot: noopAsyncString,
  createBot: noopAsync,
  updateBot: noopAsync,
  activateFromBacktest: noopAsync,
  openInMyBots: noopAsync,
  saveThenRunBacktest: async () => ({ botId: '', backtestId: '' }),
  createOrUpdateBot: noopAsyncString,
  editBot: noopAsyncVoid,
  clearCurrentBot: noopVoid,
  deleteBot: noopAsyncVoid,
  toggleBotStatus: noopAsyncVoid,
  toggleBotRunning: async () => ({ success: false, is_running: false }),
  setPracticeMode: noopVoid,
};

const Ctx = createContext<BotsContextType>(defaultCtx);

const mapRowToBot = (row: RawRow): Bot | null => {
  const id = row.id ?? null;
  if (!id) return null;

  let selectedCoins: string[] = [];
  if (row.selected_coins) {
    if (Array.isArray(row.selected_coins)) {
      selectedCoins = row.selected_coins;
    } else if (typeof row.selected_coins === 'string') {
      try {
        selectedCoins = JSON.parse(row.selected_coins);
      } catch {
        selectedCoins = [];
      }
    }
  }

  return {
    id,
    title: row.name ?? 'Unnamed Bot',
    name: row.name ?? null,
    status: null,
    exchange: row.exchange ?? null,
    strategy: row.strategy ?? null,
    runMode: row.deployment_target ?? 'paper',
    deploymentTarget: row.deployment_target ?? 'paper',
    createdAt: row.created_at ?? null,
    is_active: row.is_active ?? false,
    isActive: row.is_active ?? false,
    is_running: row.is_running ?? false,
    isRunning: row.is_running ?? false,
    is_practice: row.is_practice ?? false,
    isPractice: row.is_practice ?? false,
    profit: 0,
    total_trades: 0,
    win_rate: 0,
    isConnected: true,
    selected_coins: selectedCoins,
    selectedCoins: selectedCoins,
    quote_currency: row.quote_currency ?? 'USDT',
    quoteCurrency: row.quote_currency ?? 'USDT',
    primary_time_indicators: [],
    primaryTimeIndicators: [],
    secondary_time_enabled: false,
    secondaryTimeEnabled: false,
    signal_only_mode: false,
    signalOnlyMode: false,
    is_from_marketplace: false,
    is_premium: false,
  };
};

export const BotsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const [bots, setBots] = useState<Bot[]>([]);
  const [practiceBots, setPracticeBots] = useState<Bot[]>([]);
  const [activeBots, setActiveBots] = useState<Bot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentBot, setCurrentBot] = useState<Bot | null>(null);
  const [backtestResults] = useState<any[]>([]);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const safeSet = <T,>(setter: React.Dispatch<React.SetStateAction<T>>, v: React.SetStateAction<T>) => {
    if (mounted.current) setter(v);
  };

  const fetchBots = useCallback(async () => {
    if (!user?.id) {
      safeSet(setBots, []);
      safeSet(setPracticeBots, []);
      safeSet(setActiveBots, []);
      return;
    }
    
    safeSet(setLoading, true);
    safeSet(setError, null);

    try {
      const { data, error } = await supabase
        .from('bots')
        .select('id, user_id, name, exchange, strategy, deployment_target, quote_currency, primary_time_interval, selected_coins, created_at, updated_at, is_practice, is_active, origin, win_rate, total_trades, profit')
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[BOTS CONTEXT] fetchBots error:', error);
        safeSet(setError, error.message || 'Failed to fetch bots');
        safeSet(setBots, []);
        safeSet(setPracticeBots, []);
        safeSet(setActiveBots, []);
        return;
      }

      const mapped = (data ?? []).map(mapRowToBot).filter(Boolean) as Bot[];

      const liveBots = mapped.filter(bot => bot.deploymentTarget === 'live');
      const practiceBotsList = mapped.filter(bot => bot.deploymentTarget === 'paper');
      const activeBotsList = mapped.filter(bot => bot.is_active === true);

      safeSet(setBots, liveBots);
      safeSet(setPracticeBots, practiceBotsList);
      safeSet(setActiveBots, activeBotsList);
    } catch (err: any) {
      console.error('[BOTS CONTEXT] fetchBots error:', err);
      safeSet(setError, err.message || 'Failed to fetch bots');
      safeSet(setBots, []);
      safeSet(setPracticeBots, []);
      safeSet(setActiveBots, []);
    } finally {
      safeSet(setLoading, false);
    }
  }, [user?.id]);

  const refreshBots = useCallback(async () => {
    await fetchBots();
  }, [fetchBots]);

  const getBotById = useCallback((id: string) => {
    return bots.find((b) => b.id === id) || practiceBots.find((b) => b.id === id);
  }, [bots, practiceBots]);

  const saveBot = useCallback(async (payload: SaveBotPayload) => {
    if (!user?.id) throw new Error('Nėra prisijungimo sesijos');

    const rawStrategy = payload.strategy ?? payload.strategyKey ?? payload.strategy_key;
    const strategyKey = toStrategyKey(rawStrategy) ?? rawStrategy ?? 'trend';

    const safeName = (payload.name ?? payload.title ?? '').trim() || 'Untitled Bot';

    const signalOnly = Boolean(payload.signalOnlyMode);

    const rawSelectedCoins = payload.selectedCoins ?? payload.selected_coins;
    const selectedCoins = Array.isArray(rawSelectedCoins)
      ? rawSelectedCoins.map((c: any) =>
          typeof c === 'string' ? c : (c?.symbol ?? c?.code ?? c?.id)
        )
      : [];

    const isNewBot = !payload.id || payload.id === '';

    const payloadJsonb: any = {
      user_id: user.id,
      name: safeName,
      exchange: payload.exchange || 'binance',
      strategy_key: strategyKey,
      signal_only_mode: signalOnly,
      primary_time_interval: payload.primaryTimeInterval ?? payload.primary_time_interval ?? '15m',
      quote_currency: payload.quoteCurrency ?? payload.quote_currency ?? 'USDC',
      selected_coins: selectedCoins,
      deployment_target: payload.deploymentTarget ?? payload.deployment_target ?? 'live',
      origin: payload.origin ?? 'custom',
      run_mode: 'bot',
      exchange_fee_percentage: payload.exchangeFeePercentage ?? payload.exchange_fee_percentage ?? 0.055,
    };

    if (!isNewBot) {
      payloadJsonb.id = payload.id;
    }

    if (payload.sellStopLossEnabled !== undefined || payload.sell_stop_loss_enabled !== undefined) {
      payloadJsonb.sell_stop_loss_enabled = Boolean(payload.sellStopLossEnabled ?? payload.sell_stop_loss_enabled ?? true);
    }
    if (payload.sellStopLossPercentage !== undefined || payload.sell_stop_loss_percentage !== undefined) {
      payloadJsonb.sell_stop_loss_percentage = Number(payload.sellStopLossPercentage ?? payload.sell_stop_loss_percentage ?? 1);
    }
    if (payload.sellMaxLossAmount !== undefined || payload.sell_max_loss_amount !== undefined) {
      payloadJsonb.sell_max_loss_amount = Number(payload.sellMaxLossAmount ?? payload.sell_max_loss_amount ?? 50);
    }
    if (payload.sellStopLossUseFixedAmount !== undefined || payload.sell_stop_loss_use_fixed_amount !== undefined) {
      payloadJsonb.sell_stop_loss_use_fixed_amount = Boolean(payload.sellStopLossUseFixedAmount ?? payload.sell_stop_loss_use_fixed_amount ?? false);
    }

    if (payload.sellTrailingExitEnabled !== undefined || payload.sell_trailing_exit_enabled !== undefined) {
      payloadJsonb.sell_trailing_exit_enabled = Boolean(payload.sellTrailingExitEnabled ?? payload.sell_trailing_exit_enabled ?? false);
    }
    if (payload.sellTrailingExitUseFixedAmount !== undefined || payload.sell_trailing_exit_use_fixed_amount !== undefined) {
      payloadJsonb.sell_trailing_exit_use_fixed_amount = Boolean(payload.sellTrailingExitUseFixedAmount ?? payload.sell_trailing_exit_use_fixed_amount ?? false);
    }
    if (payload.sellTrailingExitStages !== undefined || payload.sell_trailing_exit_stages !== undefined) {
      payloadJsonb.sell_trailing_exit_stages = payload.sellTrailingExitStages ?? payload.sell_trailing_exit_stages ?? [];
    }

    if (payload.emailNotifications !== undefined || payload.email_notifications !== undefined) {
      payloadJsonb.email_notifications = Boolean(payload.emailNotifications ?? payload.email_notifications ?? true);
    }
    if (payload.webNotifications !== undefined || payload.web_notifications !== undefined) {
      payloadJsonb.web_notifications = Boolean(payload.webNotifications ?? payload.web_notifications ?? true);
    }
    if (payload.mobileNotifications !== undefined || payload.mobile_notifications !== undefined) {
      payloadJsonb.mobile_notifications = Boolean(payload.mobileNotifications ?? payload.mobile_notifications ?? false);
    }
    if (payload.soundAlerts !== undefined || payload.sound_alerts !== undefined) {
      payloadJsonb.sound_alerts = Boolean(payload.soundAlerts ?? payload.sound_alerts ?? true);
    }
    if (payload.emailFrequency !== undefined || payload.email_frequency !== undefined) {
      payloadJsonb.email_frequency = String(payload.emailFrequency ?? payload.email_frequency ?? 'instant');
    }
    if (payload.notifyOn !== undefined || payload.notify_on !== undefined) {
      payloadJsonb.notify_on = payload.notifyOn ?? payload.notify_on ?? {
        errors: true,
        trades: true,
        signals: true,
        profitLoss: true,
        marketUpdates: false,
        systemUpdates: false
      };
    }

    payloadJsonb.secondary_time_enabled = payload.secondaryTimeEnabled === true;
    payloadJsonb.secondary_time_interval = payload.secondaryTimeInterval || '1h';
    payloadJsonb.secondary_time_indicators = payload.secondaryTimeIndicators || [];

    if (payload.maxDailyTrades !== undefined) {
      payloadJsonb.max_daily_trades = Number(payload.maxDailyTrades);
    }
    if (payload.precisionPercentage !== undefined) {
      payloadJsonb.precision_percentage = Number(payload.precisionPercentage);
    }

    if (payload.timeBasedEnabled !== undefined) payloadJsonb.time_based_enabled = Boolean(payload.timeBasedEnabled);
    if (payload.closeAfterDurationEnabled !== undefined) payloadJsonb.close_after_duration_enabled = Boolean(payload.closeAfterDurationEnabled);
    if (payload.closeAfterDuration !== undefined) payloadJsonb.close_after_duration = Number(payload.closeAfterDuration);
    if (payload.closeAtSpecificTimeEnabled !== undefined) payloadJsonb.close_at_specific_time_enabled = Boolean(payload.closeAtSpecificTimeEnabled);
    if (payload.closeAtTime !== undefined) payloadJsonb.close_at_time = payload.closeAtTime;
    if (payload.closeEndOfDayEnabled !== undefined) payloadJsonb.close_end_of_day_enabled = Boolean(payload.closeEndOfDayEnabled);
    if (payload.closeDayEnd !== undefined) payloadJsonb.close_day_end = Boolean(payload.closeDayEnd);
    if (payload.closeOnFridayEveningEnabled !== undefined) payloadJsonb.close_on_friday_evening_enabled = Boolean(payload.closeOnFridayEveningEnabled);
    if (payload.closeFridayEnd !== undefined) payloadJsonb.close_friday_end = Boolean(payload.closeFridayEnd);
    if (payload.timezone !== undefined) payloadJsonb.timezone = payload.timezone;
    
    if (payload.globalProfitEnabled !== undefined) payloadJsonb.global_profit_enabled = Boolean(payload.globalProfitEnabled);
    if (payload.profitTarget !== undefined) payloadJsonb.profit_target = Number(payload.profitTarget);
    if (payload.profitAmount !== undefined) payloadJsonb.profit_amount = Number(payload.profitAmount);
    if (payload.usePercentage !== undefined) payloadJsonb.use_percentage = Boolean(payload.usePercentage);

    if (payload.percentageAmount !== undefined || payload.percentage_amount !== undefined) {
      payloadJsonb.percentage_amount = Number(payload.percentageAmount ?? payload.percentage_amount ?? 10);
    }
    if (payload.accountBalance !== undefined || payload.account_balance !== undefined) {
      payloadJsonb.account_balance = Number(payload.accountBalance ?? payload.account_balance ?? 1000);
    }
    if (payload.minAmount !== undefined || payload.min_amount !== undefined) {
      payloadJsonb.min_amount = Number(payload.minAmount ?? payload.min_amount ?? 10);
    }
    if (payload.useFixedAmount !== undefined || payload.use_fixed_amount !== undefined) {
      payloadJsonb.use_fixed_amount = Boolean(payload.useFixedAmount ?? payload.use_fixed_amount ?? false);
    }
    if (payload.maxAllocation !== undefined || payload.max_allocation !== undefined) {
      payloadJsonb.max_allocation = Number(payload.maxAllocation ?? payload.max_allocation ?? 1000);
    }

    if (payload.buyOrderType !== undefined || payload.buy_order_type !== undefined) {
      payloadJsonb.buy_order_type = payload.buyOrderType ?? payload.buy_order_type ?? 'market';
    }
    if (payload.sellOrderType !== undefined || payload.sell_order_type !== undefined) {
      payloadJsonb.sell_order_type = payload.sellOrderType ?? payload.sell_order_type ?? 'market';
    }

    if (payload.maxOpenPositions !== undefined || payload.max_open_positions !== undefined) {
      payloadJsonb.max_open_positions = Number(payload.maxOpenPositions ?? payload.max_open_positions ?? 1);
    }
    if (payload.maxOpenOrders !== undefined || payload.max_open_orders !== undefined) {
      payloadJsonb.max_open_orders = Number(payload.maxOpenOrders ?? payload.max_open_orders ?? 1);
    }
    if (payload.onlyOneOpenBuyOrder !== undefined || payload.only_one_open_buy_order !== undefined) {
      payloadJsonb.only_one_open_buy_order = Boolean(payload.onlyOneOpenBuyOrder ?? payload.only_one_open_buy_order ?? false);
    }
    if (payload.onlyOneOpenSellOrder !== undefined || payload.only_one_open_sell_order !== undefined) {
      payloadJsonb.only_one_open_sell_order = Boolean(payload.onlyOneOpenSellOrder ?? payload.only_one_open_sell_order ?? false);
    }

    if (payload.longEntryEnabled !== undefined || payload.longEnabled !== undefined || payload.long_entry_enabled !== undefined) {
      payloadJsonb.long_entry_enabled = Boolean(payload.longEntryEnabled ?? payload.longEnabled ?? payload.long_entry_enabled ?? true);
    }
    if (payload.shortEntryEnabled !== undefined || payload.shortEnabled !== undefined || payload.short_entry_enabled !== undefined) {
      payloadJsonb.short_entry_enabled = Boolean(payload.shortEntryEnabled ?? payload.shortEnabled ?? payload.short_entry_enabled ?? true);
    }
    if (payload.longEntryStrategy !== undefined || payload.long_entry_strategy !== undefined) {
      payloadJsonb.long_entry_strategy = payload.longEntryStrategy ?? payload.long_entry_strategy ?? 'trend_following';
    }
    if (payload.shortEntryStrategy !== undefined || payload.short_entry_strategy !== undefined) {
      payloadJsonb.short_entry_strategy = payload.shortEntryStrategy ?? payload.short_entry_strategy ?? 'trend_following';
    }
    if (payload.longOrderType !== undefined || payload.long_order_type !== undefined) {
      payloadJsonb.long_order_type = payload.longOrderType ?? payload.long_order_type ?? 'market';
    }
    if (payload.shortOrderType !== undefined || payload.short_order_type !== undefined) {
      payloadJsonb.short_order_type = payload.shortOrderType ?? payload.short_order_type ?? 'market';
    }

    // --- LONG DCA ACCUMULATION ---
    const longDcaEnabled = payload.longDcaEnabled ?? payload.buyTakeProfitEnabled ?? payload.buy_take_profit_enabled;
    if (longDcaEnabled !== undefined) payloadJsonb.long_dca_enabled = Boolean(longDcaEnabled);

    const longDcaFixed = payload.longDcaUseFixedAmount ?? payload.buyTakeProfitUseFixedAmount ?? payload.buy_take_profit_use_fixed_amount;
    if (longDcaFixed !== undefined) payloadJsonb.long_dca_use_fixed_amount = Boolean(longDcaFixed);

    const longDcaStages = payload.longDcaStages ?? payload.buyTakeProfitStages ?? payload.buy_take_profit_stages;
    if (longDcaStages !== undefined) {
      const useFixed = Boolean(longDcaFixed);
      payloadJsonb.long_dca_stages = (longDcaStages || []).map((stage: any) => ({
        stage: stage.stage,
        buy_pct: (stage.buyPercentage ?? stage.buy_pct ?? 0) / 100,
        target_pct: useFixed ? 0 : (stage.profitTargetPct ?? stage.target_pct ?? 0) / 100,
        target_usd: useFixed ? (stage.profitAmountUsd ?? stage.target_usd ?? 0) : null,
        enabled: stage.enabled !== false,
      }));
    }

    // --- SHORT DCA ACCUMULATION ---
    const shortDcaEnabled = payload.shortDcaEnabled ?? payload.sellTakeProfitEnabled ?? payload.sell_take_profit_enabled;
    if (shortDcaEnabled !== undefined) payloadJsonb.short_dca_enabled = Boolean(shortDcaEnabled);

    const shortDcaFixed = payload.shortDcaUseFixedAmount ?? payload.sellTakeProfitUseFixedAmount ?? payload.sell_take_profit_use_fixed_amount;
    if (shortDcaFixed !== undefined) payloadJsonb.short_dca_use_fixed_amount = Boolean(shortDcaFixed);

    const shortDcaStages = payload.shortDcaStages ?? payload.sellTakeProfitStages ?? payload.sell_take_profit_stages;
    if (shortDcaStages !== undefined) {
      const useFixed = Boolean(shortDcaFixed);
      payloadJsonb.short_dca_stages = (shortDcaStages || []).map((stage: any) => ({
        stage: stage.stage,
        sell_pct: (stage.sellPercentage ?? stage.sell_pct ?? 0) / 100,
        target_pct: useFixed ? 0 : (stage.profitTargetPct ?? stage.target_pct ?? 0) / 100,
        target_usd: useFixed ? (stage.profitAmountUsd ?? stage.target_usd ?? 0) : null,
        enabled: stage.enabled !== false,
      }));
    }

    // --- LONG PROFIT MANAGEMENT ---
    const longPmEnabled = payload.longProfitManagementEnabled ?? payload.buyTakeProfitStagesEnabled ?? payload.buy_take_profit_stages_enabled;
    if (longPmEnabled !== undefined) payloadJsonb.long_profit_management_enabled = Boolean(longPmEnabled);

    const longPmFixed = payload.longProfitManagementUseFixedAmount ?? payload.buyTakeProfitStagesUseFixedAmount ?? payload.buy_take_profit_stages_use_fixed_amount;
    if (longPmFixed !== undefined) payloadJsonb.long_profit_management_use_fixed_amount = Boolean(longPmFixed);

    const longPmStages = payload.longProfitManagementStages ?? payload.buyTakeProfitStagesData ?? payload.long_profit_management_stages;
    if (longPmStages !== undefined) {
      const useFixedPM = Boolean(longPmFixed);
      payloadJsonb.long_profit_management_stages = (longPmStages || []).map((stage: any) => ({
        stage: stage.stage,
        buy_pct: (stage.buyPercentage ?? stage.buy_pct ?? stage.sellPercentage ?? stage.sell_pct ?? 0) / 100,
        target_pct: useFixedPM ? 0 : (stage.profitTargetPct ?? stage.target_pct ?? 0) / 100,
        target_usd: useFixedPM ? (stage.profitAmountUsd ?? stage.target_usd ?? 0) : null,
        trailing_stop_pct: (stage.trailingStopPct ?? stage.trailing_stop_pct ?? 0) / 100,
        enabled: stage.enabled !== false,
      }));
    }

    const longPmInitTs = payload.longProfitManagementInitialTrailingStop ?? payload.buyTakeProfitStagesInitialTrailingStop ?? payload.buy_take_profit_stages_initial_trailing_stop;
    if (longPmInitTs !== undefined) payloadJsonb.long_profit_management_initial_trailing_stop = Number(longPmInitTs);

    const longPmDynTs = payload.longProfitManagementDynamicTrailingEnabled ?? payload.buyTakeProfitStagesDynamicTrailingEnabled ?? payload.buy_take_profit_stages_dynamic_trailing_enabled;
    if (longPmDynTs !== undefined) payloadJsonb.long_profit_management_dynamic_trailing_enabled = Boolean(longPmDynTs);

    const longPmAutoAdjust = payload.longProfitManagementAutoAdjustEnabled ?? payload.buyTakeProfitStagesAutoAdjustEnabled ?? payload.buy_take_profit_stages_auto_adjust_enabled;
    if (longPmAutoAdjust !== undefined) payloadJsonb.long_profit_management_auto_adjust_enabled = Boolean(longPmAutoAdjust);

    // --- SHORT PROFIT MANAGEMENT ---
    const shortPmEnabled = payload.shortProfitManagementEnabled ?? payload.sellTakeProfitStagesEnabled ?? payload.sell_take_profit_stages_enabled;
    if (shortPmEnabled !== undefined) payloadJsonb.short_profit_management_enabled = Boolean(shortPmEnabled);

    const shortPmFixed = payload.shortProfitManagementUseFixedAmount ?? payload.sellTakeProfitStagesUseFixedAmount ?? payload.sell_take_profit_stages_use_fixed_amount;
    if (shortPmFixed !== undefined) payloadJsonb.short_profit_management_use_fixed_amount = Boolean(shortPmFixed);

    const shortPmStages = payload.shortProfitStages ?? payload.sellTakeProfitStagesData ?? payload.sell_take_profit_stages_data;
    if (shortPmStages !== undefined) {
      const useFixedPMShort = Boolean(shortPmFixed);
      payloadJsonb.short_profit_stages = (shortPmStages || []).map((stage: any) => ({
        stage: stage.stage,
        sell_pct: (stage.sellPercentage ?? stage.sell_pct ?? stage.buyPercentage ?? stage.buy_pct ?? 0) / 100,
        target_pct: useFixedPMShort ? 0 : (stage.profitTargetPct ?? stage.target_pct ?? 0) / 100,
        target_usd: useFixedPMShort ? (stage.profitAmountUsd ?? stage.target_usd ?? 0) : null,
        trailing_stop_pct: (stage.trailingStopPct ?? stage.trailing_stop_pct ?? 0) / 100,
        enabled: stage.enabled !== false,
      }));
    }

    const shortPmInitTs = payload.shortProfitManagementInitialTrailingStop ?? payload.sellTakeProfitStagesInitialTrailingStop ?? payload.sell_take_profit_stages_initial_trailing_stop;
    if (shortPmInitTs !== undefined) payloadJsonb.short_profit_management_initial_trailing_stop = Number(shortPmInitTs);

    const shortPmDynTs = payload.shortProfitManagementDynamicTrailingEnabled ?? payload.sellTakeProfitStagesDynamicTrailingEnabled ?? payload.sell_take_profit_stages_dynamic_trailing_enabled;
    if (shortPmDynTs !== undefined) payloadJsonb.short_profit_management_dynamic_trailing_enabled = Boolean(shortPmDynTs);

    const shortPmAutoAdjust = payload.shortProfitManagementAutoAdjustEnabled ?? payload.sellTakeProfitStagesAutoAdjustEnabled ?? payload.sell_take_profit_stages_auto_adjust_enabled;
    if (shortPmAutoAdjust !== undefined) payloadJsonb.short_profit_management_auto_adjust_enabled = Boolean(shortPmAutoAdjust);

    // --- LONG TRAILING ENTRY ---
    const longTeEnabled = payload.longTrailingEntryEnabled ?? payload.buyTrailingEntryEnabled ?? payload.buy_trailing_entry_enabled;
    if (longTeEnabled !== undefined) payloadJsonb.long_trailing_entry_enabled = Boolean(longTeEnabled);

    const longTeFixed = payload.longTrailingEntryUseFixedAmount ?? payload.buyTrailingEntryUseFixedAmount ?? payload.buy_trailing_entry_use_fixed_amount;
    if (longTeFixed !== undefined) payloadJsonb.long_trailing_entry_use_fixed_amount = Boolean(longTeFixed);

    const longTeStages = payload.longTrailingEntryStages ?? payload.buyTrailingEntryStages ?? payload.buy_trailing_entry_stages;
    if (longTeStages !== undefined) payloadJsonb.long_trailing_entry_stages = longTeStages;

    // --- SHORT TRAILING ENTRY ---
    const shortTeEnabled = payload.shortTrailingEntryEnabled ?? payload.sellTrailingExitEnabled ?? payload.sell_trailing_exit_enabled;
    if (shortTeEnabled !== undefined) payloadJsonb.short_trailing_entry_enabled = Boolean(shortTeEnabled);

    const shortTeFixed = payload.shortTrailingEntryUseFixedAmount ?? payload.sellTrailingExitUseFixedAmount ?? payload.sell_trailing_exit_use_fixed_amount;
    if (shortTeFixed !== undefined) payloadJsonb.short_trailing_entry_use_fixed_amount = Boolean(shortTeFixed);

    const shortTeStages = payload.shortTrailingEntryStages ?? payload.sellTrailingExitStages ?? payload.sell_trailing_exit_stages;
    if (shortTeStages !== undefined) payloadJsonb.short_trailing_entry_stages = shortTeStages;

    // BUY/SELL CONSTRAINTS
    if (payload.onlyPositivePairs !== undefined || payload.only_positive_pairs !== undefined) {
      payloadJsonb.only_positive_pairs = Boolean(payload.onlyPositivePairs ?? payload.only_positive_pairs ?? false);
    }
    if (payload.buyPercentRange !== undefined || payload.buy_percent_range !== undefined) {
      payloadJsonb.buy_percent_range = Number(payload.buyPercentRange ?? payload.buy_percent_range ?? 0);
    }
    if (payload.buyCooldownTime !== undefined || payload.buy_cooldown_time !== undefined) {
      payloadJsonb.buy_cooldown_time = Number(payload.buyCooldownTime ?? payload.buy_cooldown_time ?? 0);
    }

    if (payload.onlyNegativePairs !== undefined || payload.only_negative_pairs !== undefined) {
      payloadJsonb.only_negative_pairs = Boolean(payload.onlyNegativePairs ?? payload.only_negative_pairs ?? false);
    }
    if (payload.sellPercentRange !== undefined || payload.sell_percent_range !== undefined) {
      payloadJsonb.sell_percent_range = Number(payload.sellPercentRange ?? payload.sell_percent_range ?? 0);
    }
    if (payload.sellCooldownTime !== undefined || payload.sell_cooldown_time !== undefined) {
      payloadJsonb.sell_cooldown_time = Number(payload.sellCooldownTime ?? payload.sell_cooldown_time ?? 0);
    }

    // PTI logic - Signal Only ALWAYS sends
    const rawPTI = payload.primaryTimeIndicators ?? payload.primary_time_indicators;
    const hasPTI = rawPTI !== undefined;

    if (signalOnly) {
      if (hasPTI) {
        let pti = ensureArray(normalizeIndicators(rawPTI));
        if (pti.length > 0 && 'id' in pti[0]) {
          pti = stripIdsForDB(pti as any);
        }
        payloadJsonb.primary_time_indicators = pti;
      } else {
        payloadJsonb.primary_time_indicators = [];
      }
    } else if (hasPTI) {
      let pti = ensureArray(normalizeIndicators(rawPTI));
      if (pti.length > 0 && 'id' in pti[0]) {
        pti = stripIdsForDB(pti as any);
      }
      if (pti.length > 0) {
        payloadJsonb.primary_time_indicators = pti;
      } else if (strategyKey) {
        const defaults = generateIndicatorsForStrategy(strategyKey);
        payloadJsonb.primary_time_indicators = defaults;
      }
    }

    const { data, error } = await supabase.rpc('f_bots_upsert_settings_v2', { p: payloadJsonb, p_user_id: user.id });

    if (error) {
      if (error.code === '23505' || error.message?.includes('already used')) {
        throw new Error('This name is already used by another bot');
      }
      if (error.code === '22023' || error.message?.includes('Invalid strategy_key')) {
        throw new Error(error.message || 'Invalid strategy selected');
      }
      throw error;
    }

    let botId = data?.id;

    if (!botId && data?.success) {
      const { data: botData, error: fetchError } = await supabase
        .from('bots')
        .select('id')
        .eq('user_id', user.id)
        .eq('name', safeName)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!fetchError && botData?.id) {
        botId = botData.id;
      }
    }

    if (!botId || typeof botId !== 'string') {
      throw new Error('No valid bot ID returned from save operation');
    }

    // 🔥 PIVERSTINIS IŠSAUGOJIMAS KIEKVIENAI KATEGORIJAI TIESIAI Į DB
    const toggleFields: Record<string, any> = {};
    if (payloadJsonb.long_entry_enabled !== undefined) toggleFields.long_entry_enabled = payloadJsonb.long_entry_enabled;
    if (payloadJsonb.short_entry_enabled !== undefined) toggleFields.short_entry_enabled = payloadJsonb.short_entry_enabled;
    
    if (payloadJsonb.long_dca_enabled !== undefined) toggleFields.long_dca_enabled = payloadJsonb.long_dca_enabled;
    if (payloadJsonb.long_dca_use_fixed_amount !== undefined) toggleFields.long_dca_use_fixed_amount = payloadJsonb.long_dca_use_fixed_amount;
    if (payloadJsonb.long_dca_stages !== undefined) toggleFields.long_dca_stages = payloadJsonb.long_dca_stages;
    
    if (payloadJsonb.short_dca_enabled !== undefined) toggleFields.short_dca_enabled = payloadJsonb.short_dca_enabled;
    if (payloadJsonb.short_dca_use_fixed_amount !== undefined) toggleFields.short_dca_use_fixed_amount = payloadJsonb.short_dca_use_fixed_amount;
    if (payloadJsonb.short_dca_stages !== undefined) toggleFields.short_dca_stages = payloadJsonb.short_dca_stages;
    
    if (payloadJsonb.long_profit_management_enabled !== undefined) toggleFields.long_profit_management_enabled = payloadJsonb.long_profit_management_enabled;
    if (payloadJsonb.long_profit_management_use_fixed_amount !== undefined) toggleFields.long_profit_management_use_fixed_amount = payloadJsonb.long_profit_management_use_fixed_amount;
    if (payloadJsonb.long_profit_management_stages !== undefined) toggleFields.long_profit_management_stages = payloadJsonb.long_profit_management_stages;
    if (payloadJsonb.long_profit_management_initial_trailing_stop !== undefined) toggleFields.long_profit_management_initial_trailing_stop = payloadJsonb.long_profit_management_initial_trailing_stop;
    if (payloadJsonb.long_profit_management_dynamic_trailing_enabled !== undefined) toggleFields.long_profit_management_dynamic_trailing_enabled = payloadJsonb.long_profit_management_dynamic_trailing_enabled;
    if (payloadJsonb.long_profit_management_auto_adjust_enabled !== undefined) toggleFields.long_profit_management_auto_adjust_enabled = payloadJsonb.long_profit_management_auto_adjust_enabled;

    if (payloadJsonb.short_profit_management_enabled !== undefined) toggleFields.short_profit_management_enabled = payloadJsonb.short_profit_management_enabled;
    if (payloadJsonb.short_profit_management_use_fixed_amount !== undefined) toggleFields.short_profit_management_use_fixed_amount = payloadJsonb.short_profit_management_use_fixed_amount;
    if (payloadJsonb.short_profit_stages !== undefined) toggleFields.short_profit_stages = payloadJsonb.short_profit_stages;
    if (payloadJsonb.short_profit_management_initial_trailing_stop !== undefined) toggleFields.short_profit_management_initial_trailing_stop = payloadJsonb.short_profit_management_initial_trailing_stop;
    if (payloadJsonb.short_profit_management_dynamic_trailing_enabled !== undefined) toggleFields.short_profit_management_dynamic_trailing_enabled = payloadJsonb.short_profit_management_dynamic_trailing_enabled;
    if (payloadJsonb.short_profit_management_auto_adjust_enabled !== undefined) toggleFields.short_profit_management_auto_adjust_enabled = payloadJsonb.short_profit_management_auto_adjust_enabled;

    if (Object.keys(toggleFields).length > 0) {
      await supabase.from('bots').update(toggleFields).eq('id', botId);
    }

    const { data: snapshot, error: fetchError } = await supabase
      .from('bots')
      .select('*')
      .eq('id', botId)
      .maybeSingle();

    if (fetchError || !snapshot) {
      throw new Error('Failed to fetch bot snapshot from DB');
    }

    return snapshot;
  }, [user?.id]);

  const deleteBot = useCallback(async (botId: string, opts?: { hard?: boolean; forceClose?: boolean }) => {
    try {
      if (!user?.id) throw new Error('Not authenticated');

      const confirmed = window.confirm(
        'Delete bot and all backtests? No recovery!\n\n' +
        'WARNING: This action cannot be undone!\n' +
        '- Bot will be permanently deleted\n' +
        '- All related backtests will be removed\n\n' +
        'Are you sure you want to continue?'
      );
      if (!confirmed) {
        return { success: false, cancelled: true };
      }

      const { data, error } = await supabase.rpc('f_delete_bot_everywhere', {
        p_bot_id: botId as any,
        p_user_id: user.id as any
      });

      if (error) {
        const errorMsg = error.message || 'Unknown error occurred';
        toast.error(`Delete failed: ${errorMsg}`);
        throw new Error(errorMsg);
      }

      if (data === false) {
        const errorMsg = 'Bot not found or already deleted';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }

      if (data && typeof data === 'object' && data.success === false) {
        const errorMsg = data.error || 'Bot not found or already deleted';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }

      await refreshBots();
      const deletedBacktests = (data && typeof data === 'object') ? (data.deleted_backtests ?? 0) : 0;
      toast.success(`Bot deleted successfully!${deletedBacktests > 0 ? ` ${deletedBacktests} related backtest(s) also removed.` : ''}`);

      return { success: true, deleted_backtests: deletedBacktests };
    } catch (error: any) {
      throw error;
    }
  }, [user?.id, refreshBots]);

  const saveThenRunBacktest = useCallback(
    async (params: {
      currentBotId?: string | null;
      name: string;
      exchange?: string | null;
      strategy?: string | null;
      runMode: RunMode;
      deploymentTarget?: DeployTarget;
      settings: any;
    }) => {
      if (!user?.id) throw new Error('Not authenticated');

      const botId = await saveBot({
        id: params.currentBotId,
        name: params.name,
        exchange: params.exchange,
        strategy: params.strategy,
        runMode: params.runMode,
        deploymentTarget: params.deploymentTarget,
        selectedCoins: params.settings.selectedCoins,
        primaryTimeIndicators: params.settings.primaryTimeIndicators,
        settings: params.settings,
      });
      
      const { data: backtestData, error: backtestError } = await supabase.rpc('f_backtests_create', { 
        p_bot_id: botId,
        p_user_id: user.id
      });

      if (backtestError) throw backtestError;
      return { botId, backtestId: backtestData };
    },
    [saveBot, user?.id]
  );

  const activateFromBacktest = useCallback(
    async (args: {
      backtestId: string;
      backtestTitle?: string;
      desiredTarget?: DeployTarget;
      runMode?: RunMode;
      userId?: string;
    }) => {
      const { backtestId, desiredTarget = 'live' } = args;
      const userId = args.userId ?? user?.id;
      if (!userId) throw new Error('User not authenticated');

      const runMode: RunMode = args.runMode ?? (desiredTarget === 'live' ? 'live' : 'paper');

      try {
        const { data, error } = await supabase.rpc('f_bots_activate_from_backtest_v6', {
          p_backtest_id: backtestId,
          p_deployment_target: desiredTarget,
          p_run_mode: runMode,
          p_user_id: userId,
        }).single();

        if (error) throw error;
        await refreshBots();
        return { id: data?.id ?? '' };
      } catch (e: any) {
        const code = e?.code ?? '';
        if (code === 'PGRST202' || code === 'PGRST204' || (e?.message ?? '').includes('404')) {
          const { data, error } = await supabase.rpc('f_bots_activate_from_backtest_v4', {
            p_backtest_id: backtestId,
            p_deployment_target: desiredTarget,
            p_run_mode: runMode,
          }).single();

          if (error) throw error;
          await refreshBots();
          return { id: data?.id ?? '' };
        } else {
          throw e;
        }
      }
    },
    [refreshBots, user?.id]
  );

  const createBot = useCallback((payload: SaveBotPayload) => saveBot(payload).then(id => ({ id: (id as any).id || id })), [saveBot]);
  const updateBot = useCallback((botId: string, payload: SaveBotPayload) => saveBot({ ...payload, id: botId }).then(id => ({ id: (id as any).id || id })), [saveBot]);
  const openInMyBots = activateFromBacktest;

  const createOrUpdateBot = useCallback(
    async (params: {
      botId?: string | null;
      name: string;
      exchange?: string | null;
      strategy?: string | null;
      runMode: RunMode;
      deploymentTarget?: DeployTarget;
      settings: any;
    }) => {
      return await saveBot({
        id: params.botId ?? undefined,
        name: params.name,
        exchange: params.exchange,
        strategy: params.strategy,
        runMode: params.runMode,
        deploymentTarget: params.deploymentTarget,
        selectedCoins: params.settings.selectedCoins,
        primaryTimeIndicators: params.settings.primaryTimeIndicators,
        settings: params.settings,
      });
    },
    [saveBot]
  );

  const editBot = useCallback(async (botId: string) => {
    const bot = getBotById(botId);
    if (bot) {
      setCurrentBot(bot);
    }
  }, [getBotById]);

  const clearCurrentBot = useCallback(() => {
    setCurrentBot(null);
  }, []);

  const toggleBotStatus = useCallback(async (botId: string, active?: boolean) => {
    try {
      if (!user?.id) throw new Error('Not authenticated');
      const { error } = await supabase.rpc('f_bots_set_active', {
        p_user_id: user.id,
        p_bot_id: botId,
        p_active: active ?? true
      });
      if (error) throw error;
      await refreshBots();
      return { success: true };
    } catch (error) {
      throw error;
    }
  }, [refreshBots, user?.id]);

  const toggleBotRunning = useCallback(async (botId: string, running?: boolean) => {
    try {
      if (!user?.id) throw new Error('Not authenticated');
      const { error } = await supabase.rpc('f_bots_set_running', {
        p_user_id: user.id,
        p_bot_id: botId,
        p_running: running ?? true 
      });
      if (error) throw error;
      await refreshBots();
      return { success: true };
    } catch (error) {
      throw error;
    }
  }, [refreshBots, user?.id]);

  const setPracticeMode = useCallback(() => {}, []);

  const value = useMemo<BotsContextType>(
    () => ({
      bots, practiceBots, activeBots, loading, isLoading: loading, error, currentBot, backtestResults, fetchBots, refreshBots, getBotById, saveBot, createBot, updateBot, activateFromBacktest, openInMyBots, saveThenRunBacktest, createOrUpdateBot, editBot, clearCurrentBot, deleteBot, toggleBotStatus, toggleBotRunning, setPracticeMode,
    }),
    [bots, practiceBots, activeBots, loading, error, currentBot, backtestResults, fetchBots, refreshBots, getBotById, saveBot, createBot, updateBot, activateFromBacktest, openInMyBots, saveThenRunBacktest, createOrUpdateBot, editBot, clearCurrentBot, deleteBot, toggleBotStatus, toggleBotRunning, setPracticeMode]
  );

  useEffect(() => {
    if (user?.id) {
      fetchBots();
    } else {
      setBots([]);
      setPracticeBots([]);
    }
  }, [user?.id, fetchBots]);

  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel('public:bots')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'bots', filter: `user_id=eq.${user.id}` },
        (payload) => {
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          const recordId = newRecord?.id || oldRecord?.id;
          if (newRecord?.deleted_at || newRecord?.status === 'deleted') {
            setBots(prev => prev.filter(b => b.id !== recordId));
            setPracticeBots(prev => prev.filter(b => b.id !== recordId));
            setActiveBots(prev => prev.filter(b => b.id !== recordId));
            return;
          }
          fetchBots();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchBots]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useBots = () => {
  const context = useContext(Ctx);
  if (context === undefined) {
    throw new Error('useBots must be used within a BotsProvider');
  }
  return context;
};

export const useBotsOptional = () => useContext(Ctx);